import {
  getTranslatedViewHeaderDescription,
  getViewHeaderDescriptionFromContext,
  useHistory,
  useNavigation,
  useSitemapItems,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  EntityMetadata,
  EntityModel,
  FormModel,
  readBaseMetadataByNameAndModule,
  readEntityModelIncludeExternal,
  ViewModel,
} from '@msdk/definitions'
import { stringFormat } from '@msdk/forms'
import { Breadcrumbs, COLORS, createComponentWrapperWithRef, styled, THEME_CONST, Typography, uiTokens } from '@msdk/ui'
import { Box } from '@mui/material'
import { Link, useMatches } from 'react-router-dom'

const CrumbLink = styled(Link)`
  text-decoration: none;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: inherit;
  font-family: inherit;
`

const MuiSitemapCrumbLink = styled('span')`
  text-decoration: none;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  color: inherit;
  line-height: 150%;
  font-family: inherit;
  cursor: pointer;
`
const NovaSitemapCrumbLink = styled('span')`
  text-decoration: underline;
  color: ${uiTokens.uiStroke.accent};
  font-size: 1rem;
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.medium};
  line-height: 150%;
  font-family: inherit;
  cursor: pointer;
`
const SitemapCrumbLink = createComponentWrapperWithRef(
  {
    mui: MuiSitemapCrumbLink,
    nova: NovaSitemapCrumbLink,
  },
  'SitemapCrumbLink',
)

const EmptyBreadCrumbContainer = styled(Box)`
  background-color: ${COLORS.white.main};
  height: ${THEME_CONST.defaultPadding}px;
  width: 100%;
`

const ActiveCrumb = styled(Typography)`
  color: ${COLORS.text.primary};
`

export function Breadcrumb() {
  const t = useTranslation()
  const matches = useMatches()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const crumb = matches.filter((match) => (match.handle as any)?.crumb)[0]
  const history = useHistory()
  const frameworkNavigate = useNavigation()

  const currentMatchEntity = crumb?.params['entityName'] as string
  const currentMatchModuleName = crumb?.params['moduleName'] as string

  const currentMatchId = crumb?.params['id'] as string
  const { context } = useUrlContext()
  const sitemapItems = useSitemapItems()

  const getCurrentCrumb = () => {
    /**
     * the breadcrumb is only visible when whe are in a form that is readonly
     * we identify a FORM route by the fact that the entityname and id parameters are not null
     * VIEW item: whe are ignoring this as from UI/UX no breadcrumb is shown on VIEWs
     * CUSTOM item: also not visible in this, it will be responsibility of the custom component to show it's own breadcrumb
     * if needed
     */
    const queryParameters = new URLSearchParams(window.location.search)
    const mode = queryParameters.get('mode')

    if (currentMatchEntity && currentMatchId && mode !== 'Edit') {
      const baseMetadata = readBaseMetadataByNameAndModule(currentMatchEntity, currentMatchModuleName) as EntityMetadata
      const entityModel = readEntityModelIncludeExternal(currentMatchEntity, baseMetadata.msProvider) as EntityModel
      const formModel = entityModel.getForm(currentMatchModuleName) as FormModel

      if (formModel.breadcrumb) {
        return <ActiveCrumb>{t(formModel.breadcrumb.idml, formModel.breadcrumb.description)}</ActiveCrumb>
      }

      const isInCreation = currentMatchId === 'Create'
      const isInEdit = mode === 'Edit' && !isInCreation

      const entityName = t(entityModel.idml, entityModel.description)
      if (isInCreation) {
        return <ActiveCrumb>{stringFormat(t('CMPSR_CREATE_NEW_ENTITY', 'Create {0}'), entityName)}</ActiveCrumb>
      }

      if (isInEdit) {
        return <ActiveCrumb>{stringFormat(t('CMPSR_EDIT_ENTITY', 'Edit {0}'), entityName)}</ActiveCrumb>
      }

      return <ActiveCrumb>{t(formModel.idml, formModel.description)}</ActiveCrumb>
    }
  }

  const getPrevCrumb = () => {
    /**
     * the breadcrumb is only visible when whe are in a form that is readonly
     * * we identify a form route by the fact that the entityname and id parameters are not null
     */
    const queryParameters = new URLSearchParams(window.location.search)
    const mode = queryParameters.get('mode')

    if (currentMatchEntity && currentMatchId && mode !== 'Edit') {
      const lastVisitedView = history.getLastVisitedView()
      const last = lastVisitedView?.params['entityName'] === currentMatchEntity ? lastVisitedView : false

      /**
       * Whe are in a form but we have not matched the previous route with the current one
       * this means that the previous url is not related to the current one, or this is the first url visited
       * whe are going to show and empty breadcrumb to indicate that there are not any link to go back to
       */
      if (!last) {
        return <Typography color="text.primary">...</Typography>
      }

      let lastMatchEntity = last?.params['entityName'] as string
      const lastMatchModuleName = last?.params['moduleName'] as string

      const moduleName = lastMatchModuleName.split(',')[0]
      let cleanModuleName = moduleName
      if (moduleName.indexOf(':') > -1) {
        const entityAndModule = moduleName.split(':')
        lastMatchEntity = entityAndModule[0]
        cleanModuleName = entityAndModule[1]
      }
      const baseMetadata = readBaseMetadataByNameAndModule(lastMatchEntity, cleanModuleName) as EntityMetadata
      const entityModel = readEntityModelIncludeExternal(lastMatchEntity, baseMetadata.msProvider) as EntityModel
      const viewModel = entityModel.getView(cleanModuleName) as ViewModel
      const idml = viewModel.breadcrumb ? viewModel.breadcrumb.idml : viewModel.idml
      const description = viewModel.breadcrumb ? viewModel.breadcrumb.description : viewModel.description
      const viewHeaderDescriptionObj = getViewHeaderDescriptionFromContext(context)
      const viewHeaderDescription = getTranslatedViewHeaderDescription(viewHeaderDescriptionObj, t)

      const label = viewHeaderDescription || t(idml, description)

      // Normalize last.search so it does not start with '?' and avoid adding a trailing '?' when empty
      const rawSearch = last.search ?? ''
      const normalizedSearch = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch
      const toUrl = normalizedSearch ? `${last.pathname}?${normalizedSearch}` : last.pathname

      return <CrumbLink to={toUrl}>{label}</CrumbLink>
    }
  }

  const getIsSingleton = () => {
    if (currentMatchEntity && currentMatchModuleName) {
      const baseMetadata = readBaseMetadataByNameAndModule(currentMatchEntity, currentMatchModuleName) as EntityMetadata
      if (baseMetadata) {
        const entityModel = readEntityModelIncludeExternal(currentMatchEntity, baseMetadata?.msProvider) as EntityModel
        return entityModel.singleton
      }
    }
    return false
  }

  const getPrevCrumbFromSId = () => {
    if (context['sId']) {
      const item = sitemapItems.find((it) => it.id === context['sId'])
      if (item && item?.idml && item?.tDescription) {
        const label = t(item.idml, item.tDescription)

        const handleClick = () => {
          frameworkNavigate(`${item.id}`, context.sitemap, context as Record<string, string>)
        }

        return <SitemapCrumbLink onClick={handleClick}>{label}</SitemapCrumbLink>
      }
    }
    return null
  }

  const prevCrumbFromSId = getPrevCrumbFromSId()
  const prevCrumb = prevCrumbFromSId ?? getPrevCrumb() ?? null
  const currentCrumb = getCurrentCrumb()

  const isSingleton = getIsSingleton()

  return prevCrumb && currentCrumb && !isSingleton ? (
    <Breadcrumbs separator=">">
      {prevCrumb}
      {currentCrumb}
    </Breadcrumbs>
  ) : isSingleton ? (
    <EmptyBreadCrumbContainer />
  ) : (
    <></>
  )
}
